// src/pages/pengamatan/buah-alami/detail-tabs/RencanaPanenBuahAlamiTab.jsx
import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper
} from '@mui/material';
import RencanaPanenBuahAlamiTableHeader from './RencanaPanenBuahAlamiTableHeader';

const RencanaPanenBuahAlamiTab = ({ pengamatanData, filteredPlots }) => {
    // Variabel untuk fase bunga
    const VARIABEL_FASE_BUNGA = {
        openHeart: 36,
        redBud125: 39,
        redBud254: 43,
        earlyCones: 47,
        midCones: 52,
        lateCones: 55,
        earlyPetals: 57,
        midPetals: 61,
        latePetals: 64,
        dryPetals: 70,
        fillingStage1: 89,
        fillingStage2: 116,
        sc0: 128,
        sc0Lebih: 130
    };

    // FUNGSI BARU: Format khusus untuk total (2 digit di belakang koma)
    const formatAngkaTotal = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        if (Math.abs(num) < 0.0001) {
            return '-';
        }
        
        // Format dengan 2 digit di belakang koma
        const formatted = num.toFixed(2);
        const parts = formatted.split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1] || '';
        
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        
        return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    };

    // FUNGSI BARU: Format khusus untuk PJP (tanpa desimal)
    const formatPJP = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        // Format tanpa desimal, langsung bulatkan
        const rounded = Math.round(num);
        return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // Fungsi untuk menambah hari ke tanggal
    const addDaysToDate = (dateString, days) => {
        if (!dateString || dateString === '-' || days === 0) return '-';
        
        try {
            // Format tanggal: DD-MM-YYYY
            const [day, month, year] = dateString.split('-');
            const date = new Date(`${year}-${month}-${day}`);
            
            // Tambah hari
            date.setDate(date.getDate() + days);
            
            // Format kembali ke DD-MM-YYYY
            const newDay = String(date.getDate()).padStart(2, '0');
            const newMonth = String(date.getMonth() + 1).padStart(2, '0');
            const newYear = date.getFullYear();
            
            return `${newDay}/${newMonth}/${newYear}`;
        } catch (error) {
            console.error('Error calculating date:', error);
            return '-';
        }
    };

    // Fungsi untuk memeriksa apakah data fase bunga kosong
    const isFaseBungaEmpty = (plotData, fieldName) => {
        const faseBunga = plotData.data_entry?.fase_bunga || {};
        const fieldValue = faseBunga[fieldName];
        
        // Periksa apakah field kosong, null, undefined, atau 0
        return !fieldValue || fieldValue === '' || fieldValue === '0' || parseFloat(fieldValue) === 0;
    };

    // Fungsi untuk menghitung rencana panen buah alami
    const calculateRencanaPanen = () => {
        // GUNAKAN filteredPlots JIKA ADA, JIKA TIDAK GUNAKAN SEMUA PLOT
        const plotsToUse = filteredPlots && filteredPlots.length > 0 
            ? filteredPlots 
            : (pengamatanData.plot ? Object.entries(pengamatanData.plot).map(([plotKey, plotData]) => ({
                plotKey,
                ...plotData
            })) : []);
        
        const tanggalPengamatan = pengamatanData.tanggal_pengamatan || '-';
        
        return plotsToUse
            .map((plot) => {
                // Periksa setiap fase bunga apakah kosong
                const isOpenHeartEmpty = isFaseBungaEmpty(plot, 'open_heart_fb_ba');
                const isRedBud125Empty = isFaseBungaEmpty(plot, 'red_bud_125_fb_ba');
                const isRedBud254Empty = isFaseBungaEmpty(plot, 'red_bud_254_fb_ba');
                const isEarlyConesEmpty = isFaseBungaEmpty(plot, 'early_cones_fb_ba');
                const isMidConesEmpty = isFaseBungaEmpty(plot, 'mid_cones_fb_ba');
                const isLateConesEmpty = isFaseBungaEmpty(plot, 'late_cones_fb_ba');
                const isEarlyPetalsEmpty = isFaseBungaEmpty(plot, 'early_petals_fb_ba');
                const isMidPetalsEmpty = isFaseBungaEmpty(plot, 'mid_petals_fb_ba');
                const isLatePetalsEmpty = isFaseBungaEmpty(plot, 'late_petals_fb_ba');
                const isDryPetalsEmpty = isFaseBungaEmpty(plot, 'dry_petals_fb_ba');
                const isFillingStage1Empty = isFaseBungaEmpty(plot, 'filling_stage_1_fb_ba');
                const isFillingStage2Empty = isFaseBungaEmpty(plot, 'filling_stage_2_fb_ba');
                const isSc0Empty = isFaseBungaEmpty(plot, 'sc0_fb_ba');
                const isSc0LebihEmpty = isFaseBungaEmpty(plot, 'sc0_ld_fb_ba');

                // Hitung tanggal hanya jika data tidak kosong
                const openHeart = isOpenHeartEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.openHeart);
                const redBud125 = isRedBud125Empty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.redBud125);
                const redBud254 = isRedBud254Empty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.redBud254);
                const earlyCones = isEarlyConesEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.earlyCones);
                const midCones = isMidConesEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.midCones);
                const lateCones = isLateConesEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.lateCones);
                const earlyPetals = isEarlyPetalsEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.earlyPetals);
                const midPetals = isMidPetalsEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.midPetals);
                const latePetals = isLatePetalsEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.latePetals);
                const dryPetals = isDryPetalsEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.dryPetals);
                const fillingStage1 = isFillingStage1Empty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.fillingStage1);
                const fillingStage2 = isFillingStage2Empty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.fillingStage2);
                const sc0 = isSc0Empty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.sc0);
                const sc0Lebih = isSc0LebihEmpty ? '-' : addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.sc0Lebih);
                
                return {
                    plot: plot.plot || plot.plotKey,
                    luasPlot: plot.luas_plot || '-',
                    pjp: plot.pjp || '-',
                    dataEntry: plot.data_entry || {},
                    
                    // Data fase bunga untuk rencana panen (dalam format tanggal)
                    openHeart,
                    redBud125,
                    redBud254,
                    earlyCones,
                    midCones,
                    lateCones,
                    earlyPetals,
                    midPetals,
                    latePetals,
                    dryPetals,
                    fillingStage1,
                    fillingStage2,
                    sc0,
                    sc0Lebih,
                    
                    // Simpan status untuk perhitungan total
                    _status: {
                        isOpenHeartEmpty,
                        isRedBud125Empty,
                        isRedBud254Empty,
                        isEarlyConesEmpty,
                        isMidConesEmpty,
                        isLateConesEmpty,
                        isEarlyPetalsEmpty,
                        isMidPetalsEmpty,
                        isLatePetalsEmpty,
                        isDryPetalsEmpty,
                        isFillingStage1Empty,
                        isFillingStage2Empty,
                        isSc0Empty,
                        isSc0LebihEmpty
                    }
                };
            })
            .sort((a, b) => {
                if (!isNaN(a.plot) && !isNaN(b.plot)) {
                    return parseInt(a.plot) - parseInt(b.plot);
                }
                return a.plot.localeCompare(b.plot);
            });
    };

    // FUNGSI BARU: Menghitung total dari semua plot untuk Rencana Panen
    const calculateTotalRow = (rencanaPanenData) => {
        if (rencanaPanenData.length === 0) return null;

        // Kumpulkan data total dari semua plot
        let totalLuasPlot = 0;
        let totalPJP = 0;

        // Data untuk menghitung fase bunga total
        let totalFaseBunga = {
            open_heart_fb_ba: 0,
            red_bud_125_fb_ba: 0,
            red_bud_254_fb_ba: 0,
            early_cones_fb_ba: 0,
            mid_cones_fb_ba: 0,
            late_cones_fb_ba: 0,
            early_petals_fb_ba: 0,
            mid_petals_fb_ba: 0,
            late_petals_fb_ba: 0,
            dry_petals_fb_ba: 0,
            filling_stage_1_fb_ba: 0,
            filling_stage_2_fb_ba: 0,
            sc0_fb_ba: 0,
            sc0_ld_fb_ba: 0
        };

        // Kumpulkan data dari semua plot
        rencanaPanenData.forEach(item => {
            const dataEntry = item.dataEntry || {};
            const faseBunga = dataEntry.fase_bunga || {};
            
            const plotLuasPlot = parseFloat(item.luasPlot) || 0;
            const plotPJP = parseFloat(item.pjp) || 0;

            totalLuasPlot += plotLuasPlot;
            totalPJP += plotPJP;

            // Kumpulkan data fase bunga
            Object.keys(totalFaseBunga).forEach(key => {
                totalFaseBunga[key] += parseFloat(faseBunga[key]) || 0;
            });
        });

        const tanggalPengamatan = pengamatanData.tanggal_pengamatan || '-';

        // Tentukan apakah setiap fase bunga memiliki data (jika total > 0 maka ada data)
        const hasOpenHeartData = totalFaseBunga.open_heart_fb_ba > 0;
        const hasRedBud125Data = totalFaseBunga.red_bud_125_fb_ba > 0;
        const hasRedBud254Data = totalFaseBunga.red_bud_254_fb_ba > 0;
        const hasEarlyConesData = totalFaseBunga.early_cones_fb_ba > 0;
        const hasMidConesData = totalFaseBunga.mid_cones_fb_ba > 0;
        const hasLateConesData = totalFaseBunga.late_cones_fb_ba > 0;
        const hasEarlyPetalsData = totalFaseBunga.early_petals_fb_ba > 0;
        const hasMidPetalsData = totalFaseBunga.mid_petals_fb_ba > 0;
        const hasLatePetalsData = totalFaseBunga.late_petals_fb_ba > 0;
        const hasDryPetalsData = totalFaseBunga.dry_petals_fb_ba > 0;
        const hasFillingStage1Data = totalFaseBunga.filling_stage_1_fb_ba > 0;
        const hasFillingStage2Data = totalFaseBunga.filling_stage_2_fb_ba > 0;
        const hasSc0Data = totalFaseBunga.sc0_fb_ba > 0;
        const hasSc0LebihData = totalFaseBunga.sc0_ld_fb_ba > 0;

        // Hitung tanggal hanya jika ada data
        const openHeart = hasOpenHeartData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.openHeart) : '-';
        const redBud125 = hasRedBud125Data ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.redBud125) : '-';
        const redBud254 = hasRedBud254Data ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.redBud254) : '-';
        const earlyCones = hasEarlyConesData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.earlyCones) : '-';
        const midCones = hasMidConesData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.midCones) : '-';
        const lateCones = hasLateConesData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.lateCones) : '-';
        const earlyPetals = hasEarlyPetalsData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.earlyPetals) : '-';
        const midPetals = hasMidPetalsData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.midPetals) : '-';
        const latePetals = hasLatePetalsData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.latePetals) : '-';
        const dryPetals = hasDryPetalsData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.dryPetals) : '-';
        const fillingStage1 = hasFillingStage1Data ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.fillingStage1) : '-';
        const fillingStage2 = hasFillingStage2Data ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.fillingStage2) : '-';
        const sc0 = hasSc0Data ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.sc0) : '-';
        const sc0Lebih = hasSc0LebihData ? addDaysToDate(tanggalPengamatan, 130 - VARIABEL_FASE_BUNGA.sc0Lebih) : '-';

        return {
            plot: 'Total',
            luasPlot: formatAngkaTotal(totalLuasPlot),
            pjp: formatPJP(totalPJP),
            
            // Data fase bunga untuk rencana panen (dalam format tanggal)
            openHeart,
            redBud125,
            redBud254,
            earlyCones,
            midCones,
            lateCones,
            earlyPetals,
            midPetals,
            latePetals,
            dryPetals,
            fillingStage1,
            fillingStage2,
            sc0,
            sc0Lebih
        };
    };

    const rencanaPanenData = calculateRencanaPanen();
    const totalData = calculateTotalRow(rencanaPanenData);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Data Rencana Panen Buah Alami
            </Typography>
            
            <Card>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <RencanaPanenBuahAlamiTableHeader/>
                        <TableBody>
                            {rencanaPanenData.map((item, index) => (
                                <TableRow key={index}>
                                    {/* Plot */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {item.plot}
                                    </TableCell>
                                    
                                    {/* Luas */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {item.luasPlot}
                                    </TableCell>
                                    
                                    {/* PJP - GUNAKAN formatPJP di sini */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {formatPJP(item.pjp)}
                                    </TableCell>
                                    
                                    {/* Fase Bunga - Data (Tanggal Rencana Panen) */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.openHeart}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.redBud125}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.redBud254}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.earlyCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.midCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.lateCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.earlyPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.midPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.latePetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.dryPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.fillingStage1}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.fillingStage2}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.sc0}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                                        {item.sc0Lebih}
                                    </TableCell>
                                </TableRow>
                            ))}
                            
                            {/* ROW TOTAL */}
                            {totalData && (
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.plot}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.luasPlot}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.pjp}
                                    </TableCell>
                                    
                                    {/* Fase Bunga - Total */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.openHeart}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.redBud125}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.redBud254}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.earlyCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.midCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.lateCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.earlyPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.midPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.latePetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.dryPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.fillingStage1}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.fillingStage2}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.sc0}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="center">
                                        {totalData.sc0Lebih}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {rencanaPanenData.length === 0 && (
                    <Typography color="text.secondary" fontStyle="italic" sx={{ mt: 2 }}>
                        Tidak ada data rencana panen buah alami
                    </Typography>
                )}
            </Card>
        </Box>
    );
};

export default RencanaPanenBuahAlamiTab;